<template>
  <div class="list-container">
    <!-- Organization Selector Tabs -->
    <div v-if="organizations.length > 1" class="tabs">
      <button 
        v-for="organization in organizations" 
        :key="organization.id"
        :class="{ 'active': organization.id === selectedOrganization?.id }" 
        @click="selectOrganization(organization.id)"
      >
        {{ organization.name }}
      </button>
    </div>

    <!-- Organization Header -->
    <div v-if="selectedOrganization" class="list-header">
      <div class="title-stack">
        <ImageThumbnail
          v-if="selectedOrganization.logo_url"
          :url="selectedOrganization.logo_url"
          max-height="100px"
        />
        <h1>{{ selectedOrganization.name }}</h1>
      </div>
      <div class="spacer"></div>
    </div>

    <!-- Channel Selector -->
    <div v-if="channels.length > 1" class="tabs">
      <button 
        v-for="channel in channels" 
        :key="channel.id"
        :class="{ 'active': channel.id === selectedChannel?.id }" 
        @click="selectChannel(channel.id)"
      >
        {{ channel.name }}
      </button>
    </div>

    <!-- Video Files List -->
    <div class="list-header">
      <h1>Video Files List</h1>

      <router-link
        v-if="selectedChannel && canCreateVideo(selectedOrganization.id)"
        :to="`/channels/${selectedChannel.id}/videofiles/new`"
        class="important-button big"
      >
        <font-awesome-icon icon="plus" /> Upload Video File
      </router-link>
    </div>

    <VideoFileList 
      v-if="selectedChannel" 
      :channel-id="selectedChannel.id" 
      :organization-id="selectedOrganization.id"
      @update="fetchChannel"
    />
    <div v-else-if="selectedOrganization" class="message no-data">
      Your organization needs a channel to manage video files.
    </div>
    <div v-else>
      <div class="message no-data">
        You need to be a member of an organization to see video files.
      </div>
      <div class="message no-data">
        <router-link
          :to="`/organizations/new`"
          class="important-button big"
        >
          <font-awesome-icon icon="plus" /> New Organization
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import VideoFileList from '@/components/VideoFileList.vue';
import ImageThumbnail from '@/components/ImageThumbnail.vue';
import { FontAwesomeIcon } from '@/scripts/icons';
import { handleApiError } from '@/scripts/shared-utils';
import { canCreateVideo } from '@/scripts/permissions';

const organizations = ref([]);
const selectedOrganization = ref(null);

const channels = ref([]);
const selectedChannel = ref(null);

const fetchOrganizations = async () => {
  try {
    const { data } = await axios.get('/api/users/me/organizations');
    organizations.value = data;
    if (organizations.value.length > 0) {
      selectedOrganization.value = organizations.value[0];
    } else {
      selectedOrganization.value = null;
    }
    fetchOrganizationChannels();
  } catch (error) {
    handleApiError(error, 'fetching organizations');
  }
};

const fetchOrganizationChannels = async () => {
  if (!selectedOrganization.value) {
    channels.value = [];
    selectedChannel.value = null;
    return;
  }

  try {
    const { data } = await axios.get(`/api/organizations/${selectedOrganization.value.id}/channels`);
    channels.value = data;
    if (channels.value.length > 0) {
      selectedChannel.value = channels.value[0];
    } else {
      selectedChannel.value = null;
    }
  } catch (error) {
    handleApiError(error, 'fetching channels');
  }
};

const fetchChannel = async () => {
  if (!selectedChannel.value) return;
  
  try {
    const response = await axios.get(`/api/channels/${selectedChannel.value.id}`);
    if (response.status === 200) {
      // Update channel details if needed
    }
  } catch (error) {
    handleApiError(error, 'refreshing channel details');
  }
};

const selectOrganization = (id) => {
  selectedOrganization.value = organizations.value.find(org => org.id === id) || null;
  fetchOrganizationChannels();
};

const selectChannel = (id) => {
  selectedChannel.value = channels.value.find(channel => channel.id === id) || null;
};

onMounted(() => {
  fetchOrganizations();
});
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/shared-tabs.css';
</style>
